// ============================================================
// contactFormat.ts — the email-address and phone-number rule for every field
// that holds one: the client record, the case's copy, bill-email consent and
// the bill / credit-note "To". Mirrors the server (backend/src/lib/contactFormat).
//   • Email: local@domain.tld — no spaces, one @, a real-looking domain with a
//     2+ letter ending; at most 254 characters.
//   • Phone (UK-friendly, not strict): digits with brackets, dashes, dots or
//     spaces as typed (e.g. "(0113) 496-0777"), an optional leading +, 10 to
//     15 digits. Stored as typed.
// Blank is not a format error — "required" is a separate rule.
// ============================================================

const EMAIL_RE = /^[^\s@,;<>()"[\]\\]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/

export const isValidEmail = (v: string): boolean => {
  const e = (v ?? '').trim()
  if (e.length === 0 || e.length > 254) return false
  if (!EMAIL_RE.test(e)) return false
  const local = e.slice(0, e.lastIndexOf('@'))
  return !local.startsWith('.') && !local.endsWith('.') && !local.includes('..')
}

export const isValidPhone = (v: string): boolean => {
  const p = (v ?? '').trim()
  // Digits plus the usual separators — brackets, dashes, dots, spaces — and
  // an optional leading +. Stored as typed.
  if (!/^\+?[0-9\s().-]+$/.test(p)) return false
  const digits = p.replace(/\D/g, '').length
  return digits >= 10 && digits <= 15
}

export const EMAIL_FORMAT_ERROR = 'Enter a valid email address, like name@example.com.'
export const PHONE_FORMAT_ERROR = 'Enter a valid phone number with at least 10 digits, like 07700 900123 or (0113) 496-0777.'

// Autosave: an email / phone that is still being typed (non-blank, not yet
// valid) is HELD BACK — removed from `changed` (mutated) and returned — so a
// half-typed "ann@exa" is never sent (the server would refuse it and the
// refusal would put the old value back mid-typing). It's sent on the first
// save after it becomes valid; Save on the card shows the FieldError.
// `keys` names the payload's columns; `flat` is the whole current payload.
export const holdInvalidContact = (
  changed: Record<string, unknown>,
  flat: Record<string, unknown>,
  keys: { email: string; phone: string; hasEmail: string },
): string[] => {
  const held: string[] = []
  const email = changed[keys.email]
  if (typeof email === 'string' && flat[keys.hasEmail] !== false && emailFormatError(email)) held.push(keys.email)
  const phone = changed[keys.phone]
  if (typeof phone === 'string' && phoneFormatError(phone)) held.push(keys.phone)
  for (const k of held) delete changed[k]
  return held
}

// The message for a typed (non-blank) value that isn't valid, else null.
export const emailFormatError = (v: string | null | undefined): string | null =>
  (v ?? '').trim() && !isValidEmail(v as string) ? EMAIL_FORMAT_ERROR : null
export const phoneFormatError = (v: string | null | undefined): string | null =>
  (v ?? '').trim() && !isValidPhone(v as string) ? PHONE_FORMAT_ERROR : null
